import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const DIRECT = "Direct / No Dealer";

export function DealerSelect({ zone, dealerInvolved, dealerRecordId, onChange, testid = "dealer-select" }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);

  const { data } = useQuery({
    queryKey: ["dealers", zone || "all"],
    queryFn: async () => (await api.get("/dealers", { params: { zone: zone || "" } })).data,
  });
  const items = data?.items || [];

  let sel = "";
  if (dealerRecordId && items.some((d) => d.id === dealerRecordId)) sel = dealerRecordId;
  else if (dealerInvolved === DIRECT) sel = "__direct";
  else if (dealerInvolved) sel = "__current";

  const addDealer = async () => {
    if (!newName.trim()) { toast.error("Enter the dealer name"); return; }
    setSaving(true);
    try {
      const { data: d } = await api.post("/dealers", { name: newName.trim(), zone: zone || "West" });
      qc.invalidateQueries({ queryKey: ["dealers"] });
      onChange({ dealer_involved: d.name, dealer_record_id: d.id });
      toast.success(d.existing ? `"${d.name}" already exists — linked it` : `Dealer "${d.name}" saved to the Dealers page`);
      setAdding(false); setNewName("");
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  if (adding) {
    return (
      <div className="flex gap-1.5">
        <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New dealer name…" className="h-9" autoFocus data-testid={`${testid}-new-name`} />
        <Button size="sm" onClick={addDealer} disabled={saving} className="h-9 bg-slate-900 hover:bg-slate-800" data-testid={`${testid}-new-save`}>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAdding(false)} className="h-9">Cancel</Button>
      </div>
    );
  }

  return (
    <Select
      value={sel}
      onValueChange={(v) => {
        if (v === "__new") { setAdding(true); return; }
        if (v === "__direct") { onChange({ dealer_involved: DIRECT, dealer_record_id: null }); return; }
        if (v === "__current") return;
        const d = items.find((x) => x.id === v);
        onChange({ dealer_involved: d?.name || "", dealer_record_id: v });
      }}
    >
      <SelectTrigger className="h-9" data-testid={testid}>
        <SelectValue placeholder={zone ? `Dealers in ${zone}` : "Select zone first / all dealers"} />
      </SelectTrigger>
      <SelectContent className="max-h-64">
        <SelectItem value="__direct">{DIRECT}</SelectItem>
        {sel === "__current" && <SelectItem value="__current">{dealerInvolved} (from import)</SelectItem>}
        {items.map((d) => (
          <SelectItem key={d.id} value={d.id}>
            {d.name}{d.city ? ` · ${d.city}` : ""}{d.sub_region && d.sub_region !== d.zone ? ` (${d.sub_region})` : ""}
          </SelectItem>
        ))}
        <SelectItem value="__new">
          <span className="flex items-center gap-1.5 text-brand font-medium"><Plus className="h-3.5 w-3.5" /> Other — add new dealer…</span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
